import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: '0F766E' } };
const HEADER_FONT = { bold: true, color: { argb: 'FFFFFF' }, size: 10 };
const TITLE_FONT = { bold: true, size: 14, color: { argb: '0F172A' } };
const HINT_FONT = { italic: true, size: 9, color: { argb: '475569' } };
const COUNTED_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEF3C7' } };
const THIN_BORDER = {
  top: { style: 'thin', color: { argb: 'CBD5E1' } },
  bottom: { style: 'thin', color: { argb: 'CBD5E1' } },
  left: { style: 'thin', color: { argb: 'CBD5E1' } },
  right: { style: 'thin', color: { argb: 'CBD5E1' } }
};

const HEADERS = [
  'id_ligne',
  'medicament',
  'lot',
  'peremption',
  'qte_systeme',
  'qte_comptee',
  'ecart',
  'unite'
];

function stamp() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`;
}

function expiryText(value) {
  if (!value) return '';
  return String(value).slice(0, 10);
}

function cellText(cell) {
  if (!cell || cell.value == null || cell.value === '') return '';
  const v = cell.value;
  if (typeof v === 'object') {
    if (v.richText) return v.richText.map((t) => t.text).join('');
    if (v.text) return String(v.text);
    if (v.result != null && v.result !== '') return String(v.result);
    if (v instanceof Date) return v.toISOString().slice(0, 10);
  }
  return String(v).trim();
}

function normHeader(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

export async function exportInventoryCountSheet(session) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'FUNTOA SMIE';
  const ws = wb.addWorksheet('Comptage', {
    views: [{ state: 'frozen', ySplit: 6 }]
  });

  ws.columns = [
    { width: 12 },
    { width: 36 },
    { width: 16 },
    { width: 14 },
    { width: 14 },
    { width: 14 },
    { width: 12 },
    { width: 12 }
  ];

  ws.mergeCells(1, 1, 1, 8);
  const title = ws.getCell(1, 1);
  title.value = `Feuille de comptage — ${session.session_number || ''}`;
  title.font = TITLE_FONT;

  ws.mergeCells(2, 1, 2, 8);
  ws.getCell(2, 1).value = `Site : ${session.site_name || ''}    Statut : ${session.status || ''}`;
  ws.getCell(2, 1).font = { size: 10, color: { argb: '334155' } };

  ws.mergeCells(4, 1, 4, 8);
  ws.getCell(4, 1).value =
    'Remplissez uniquement la colonne jaune qte_comptee. Réimportez ce fichier sans modifier id_ligne. Les cellules vides sont ignorées (le lot reste non compté).';
  ws.getCell(4, 1).font = HINT_FONT;
  ws.getRow(4).height = 28;
  ws.getCell(4, 1).alignment = { wrapText: true, vertical: 'middle' };

  const headerRow = ws.getRow(6);
  headerRow.height = 22;
  HEADERS.forEach((label, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = label;
    cell.fill = HEADER_FILL;
    cell.font = HEADER_FONT;
    cell.border = THIN_BORDER;
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
  });

  const lines = session.lines || [];
  lines.forEach((line, index) => {
    const rowNum = 7 + index;
    const row = ws.getRow(rowNum);
    const theoretical = Number(line.theoretical_qty || 0);
    row.getCell(1).value = Number(line.id);
    row.getCell(2).value = line.medication_name || '';
    row.getCell(3).value = line.lot_number || '';
    row.getCell(4).value = expiryText(line.expiry_date);
    row.getCell(5).value = theoretical;
    row.getCell(6).value =
      line.counted_qty === null || line.counted_qty === undefined ? null : Number(line.counted_qty);
    row.getCell(7).value = {
      formula: `IF(F${rowNum}="","",F${rowNum}-E${rowNum})`
    };
    row.getCell(8).value = line.unit_name || '';
    for (let c = 1; c <= 8; c += 1) {
      const cell = row.getCell(c);
      cell.border = THIN_BORDER;
      if (c === 1 || c === 5 || c === 6 || c === 7) {
        cell.alignment = { horizontal: 'right' };
      }
    }
    row.getCell(6).fill = COUNTED_FILL;
    row.getCell(6).protection = { locked: false };
  });

  ws.getColumn(1).hidden = false;
  try {
    await ws.protect('funtoa-inventory', {
      selectLockedCells: true,
      selectUnlockedCells: true
    });
  } catch {
    /* protection is best-effort in some ExcelJS builds */
  }

  const buf = await wb.xlsx.writeBuffer();
  const safeNumber = String(session.session_number || 'inventaire').replace(/[^\w-]+/g, '_');
  saveAs(
    new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }),
    `inventaire_${safeNumber}_${stamp()}.xlsx`
  );
}

export async function parseInventoryCountSheet(file) {
  const buf = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  const ws = wb.worksheets[0];
  if (!ws) {
    throw new Error('Fichier Excel vide.');
  }

  let headerRowNum = 0;
  let idCol = 0;
  let countedCol = 0;
  const scanTo = Math.min(ws.rowCount || 20, 25);
  for (let r = 1; r <= scanTo; r += 1) {
    const found = {};
    ws.getRow(r).eachCell({ includeEmpty: false }, (cell, colNumber) => {
      const key = normHeader(cellText(cell));
      if (key === 'id_ligne' || key === 'id') found.id = colNumber;
      if (
        key === 'qte_comptee' ||
        key === 'quantite_comptee' ||
        key === 'qte_physique' ||
        key === 'comptee'
      ) {
        found.counted = colNumber;
      }
    });
    if (found.id && found.counted) {
      headerRowNum = r;
      idCol = found.id;
      countedCol = found.counted;
      break;
    }
  }

  if (!headerRowNum) {
    throw new Error('Colonnes id_ligne et qte_comptee introuvables dans le fichier.');
  }

  const lines = [];
  for (let r = headerRowNum + 1; r <= (ws.rowCount || 0); r += 1) {
    const row = ws.getRow(r);
    const idRaw = cellText(row.getCell(idCol));
    if (!idRaw) continue;
    const id = parseInt(idRaw, 10);
    if (!Number.isInteger(id) || id < 1) continue;

    const countedCell = row.getCell(countedCol);
    const raw = countedCell.value;
    if (raw === null || raw === undefined || raw === '') continue;
    const countedText = cellText(countedCell);
    if (!countedText) continue;
    const counted = parseInt(countedText, 10);
    if (!Number.isInteger(counted) || counted < 0) {
      throw new Error(`Quantité invalide à la ligne Excel ${r} (id_ligne ${id}).`);
    }
    lines.push({ id, counted_qty: counted });
  }

  return lines;
}
